import getConfig from 'next/config';
import { useEffect, useState } from 'react';
import {
  DesktopVisitorHeaderBeta,
  HeaderDataProviderBeta,
  MobileVisitorHeaderBeta,
  VisitorHeaderBeta,
  CareCategory,
  getFindCareNavLinks,
  HeaderDataProviderProps,
} from '@care/navigation';

import useResponsive from '@/components/hooks/useResponsive';
import useTestFlags from '@/components/hooks/useTestFlags';
import CustomMobileVisitorHeader from '@/components/pages/visitorHome/GreaterCareLayout/Hero/CustomMobileVisitorHeader';
import { CLIENT_FEATURE_FLAGS } from '@/constants';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';

const {
  publicRuntimeConfig: { CZEN_GENERAL },
} = getConfig();

const getFindCareMenuLinks = (
  isAdultCareRoutesFlagEnabled: boolean
): HeaderDataProviderProps['findCareMenuLinks'] => {
  const findCareMenuLinksUpdated = getFindCareNavLinks(CZEN_GENERAL);
  if (isAdultCareRoutesFlagEnabled) {
    return {
      ...findCareMenuLinksUpdated,
      items: findCareMenuLinksUpdated?.items.map((careMenuLinkItem) => {
        if (!('kind' in careMenuLinkItem) && careMenuLinkItem?.id === CareCategory.AdultCare) {
          return {
            ...careMenuLinkItem,
            items: careMenuLinkItem?.items.map((adultCareItem) => {
              if (adultCareItem?.kind === 'group' && adultCareItem?.id === 'adult-care-links') {
                return {
                  ...adultCareItem,
                  items: adultCareItem?.items.map((adultCareLinkItem) => {
                    if (
                      adultCareLinkItem?.kind === 'link' &&
                      adultCareLinkItem?.href.includes(
                        '/app/enrollment/seeker/sc?serviceIdForMember=adultCare'
                      )
                    ) {
                      return {
                        ...adultCareLinkItem,
                        href: `${CZEN_GENERAL}/app/enrollment/seeker/ac?serviceIdForMember=adultCare`,
                      };
                    }

                    return adultCareLinkItem;
                  }),
                };
              }
              return adultCareItem;
            }),
          };
        }
        return careMenuLinkItem;
      }),
    };
  }

  return findCareMenuLinksUpdated;
};

const Header = ({ isHomepage = false }: { isHomepage?: boolean }) => {
  const [isTestExposureFired, setIsTestExposureFired] = useState(false);
  const { isMobile, isDesktopLG } = useResponsive();
  const { vgnMwCta, adultCareRoutesFlag } = useTestFlags();
  const isLogInCtaTestEnabled = [2, 3].includes(vgnMwCta?.value) && isMobile && isHomepage;
  const isAdultCareRoutesFlagEnabled = adultCareRoutesFlag?.value === 2;

  const findCareMenuLinksUpdated = getFindCareMenuLinks(isAdultCareRoutesFlagEnabled);

  useEffect(() => {
    if (isMobile && !isTestExposureFired) {
      AnalyticsHelper.logTestExposure(CLIENT_FEATURE_FLAGS.GROWTH_VHP_VGN_MW_CTA, vgnMwCta);
      setIsTestExposureFired(true);
    }
  }, [vgnMwCta, isMobile, isTestExposureFired]);

  if (isLogInCtaTestEnabled) {
    return (
      <HeaderDataProviderBeta
        logAnalyticsEvent={AnalyticsHelper.logEvent}
        baseUrl={CZEN_GENERAL}
        findCareMenuLinks={findCareMenuLinksUpdated}>
        <CustomMobileVisitorHeader
          transparent={isHomepage}
          variant={isHomepage ? 'inverse' : 'default'}
        />
      </HeaderDataProviderBeta>
    );
  }

  if (isAdultCareRoutesFlagEnabled) {
    return (
      <HeaderDataProviderBeta
        logAnalyticsEvent={AnalyticsHelper.logEvent}
        baseUrl={CZEN_GENERAL}
        findCareMenuLinks={findCareMenuLinksUpdated}>
        {isDesktopLG ? (
          <DesktopVisitorHeaderBeta
            transparent={isHomepage}
            variant={isHomepage ? 'inverse' : 'default'}
          />
        ) : (
          <MobileVisitorHeaderBeta
            transparent={isHomepage}
            variant={isHomepage ? 'inverse' : 'default'}
          />
        )}
      </HeaderDataProviderBeta>
    );
  }

  return (
    <VisitorHeaderBeta
      baseUrl={CZEN_GENERAL}
      transparent={isHomepage}
      variant={isHomepage ? 'inverse' : 'default'}
      logAnalyticsEvent={AnalyticsHelper.logEvent}
    />
  );
};

export default Header;
